import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./AudioPreview.module.less";
import CommonNavBar from "views/components/CommonNavBar";
import { useState, useRef, useEffect } from "react";
import DSBridge from "dsbridge";
import { getAudioPreviewV3 } from "api/home";
import { Toast } from "react-vant";

const AudioPreview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isNative, setIsNative] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioAvailable, setAudioAvailable] = useState(false);
  const [showList, setShowList] = useState(false);
  const [categories, setCategories] = useState([]);
  const [audioList, setAudioList] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [currentAudioId, setCurrentAudioId] = useState("");
  const [loopMode, setLoopMode] = useState(
    () => localStorage.getItem("utalk_audio_loop_mode") || "list",
  ); // "list" | "single"
  const progressBoxRef = useRef(null);
  const buttonRef = useRef(null);
  const touchOffsetRef = useRef(0);
  const audioRef = useRef(null);
  const LoadingRef = useRef(null);
  const shouldAutoPlayRef = useRef(false);

  // 格式化时间
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // 计算按钮和进度条的实际像素位置
  const calculatePixelPositions = () => {
    if (!buttonRef.current || !progressBoxRef.current) {
      return { buttonLeft: "0px", progressWidth: "0px" };
    }

    const buttonWidth = buttonRef.current.getBoundingClientRect().width;
    const containerWidth = progressBoxRef.current.getBoundingClientRect().width;
    const maxPosition = containerWidth - buttonWidth;

    let pixelPosition;
    if (progress <= 0) {
      pixelPosition = 0;
    } else if (progress >= 100) {
      pixelPosition = maxPosition;
    } else {
      pixelPosition = (progress / 100) * maxPosition;
    }

    return {
      buttonLeft: `${pixelPosition}px`,
      progressWidth: `${pixelPosition + buttonWidth / 2}px`,
    };
  };

  const calculateProgress = (touch) => {
    if (!progressBoxRef.current || !buttonRef.current) return;

    const rect = progressBoxRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const buttonWidth = buttonRect.width;
    const maxPosition = rect.width - buttonWidth;

    // 计算触摸点相对于进度条的位置，并考虑偏移量
    const touchX = touch.clientX - rect.left - touchOffsetRef.current;

    // 限制在有效范围内
    const boundedOffsetX = Math.max(0, Math.min(maxPosition, touchX));
    const newProgress = (boundedOffsetX / maxPosition) * 100;

    return Math.max(0, Math.min(100, newProgress));
  };

  const handleTouchStart = (e) => {
    if (!audioAvailable) return;
    if (!progressBoxRef.current || !buttonRef.current) return;

    const touch = e.touches[0];
    const rect = progressBoxRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const buttonWidth = buttonRect.width;

    // 计算触摸点相对于进度条的位置
    const touchX = touch.clientX - rect.left;
    // 计算按钮中心点相对于进度条的位置
    const buttonX = buttonRect.left - rect.left + buttonWidth / 2;

    // 如果点击位置在按钮附近，设置偏移量并开始拖动
    if (Math.abs(touchX - buttonX) < buttonWidth) {
      touchOffsetRef.current = touchX - buttonX;
      setIsDragging(true);
    } else {
      // 如果点击位置不在按钮附近，直接设置新的进度
      const maxPosition = rect.width - buttonWidth;
      const boundedOffsetX = Math.max(0, Math.min(maxPosition, touchX));
      const newProgress = (boundedOffsetX / maxPosition) * 100;
      setProgress(newProgress);
      if (audioRef.current) {
        audioRef.current.currentTime = (newProgress / 100) * duration;
      }
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      e.preventDefault();
      const newProgress = calculateProgress(e.touches[0]);
      if (newProgress !== undefined) {
        setProgress(newProgress);
      }
    }
  };

  const handleTouchEnd = () => {
    if (isDragging && audioRef.current) {
      audioRef.current.currentTime = (progress / 100) * duration;
    }
    setIsDragging(false);
    touchOffsetRef.current = 0;
  };

  const togglePlay = () => {
    if (!audioAvailable) {
      Toast.fail("当前音频未加载成功");
      return;
    }
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // 初始化音频
  const initAudio = (audioUrl, autoPlay = false) => {
    if (!audioRef.current || !audioUrl) return;

    if (LoadingRef.current?.clear) {
      LoadingRef.current.clear();
    }
    Toast.clear();

    shouldAutoPlayRef.current = autoPlay;
    audioRef.current.src = audioUrl;
    LoadingRef.current = Toast.loading({
      message: "正在加载音频...",
      forbidClick: true,
      duration: 0,
    });
  };

  useEffect(() => {
    const progressBox = progressBoxRef.current;
    if (!progressBox) return;

    progressBox.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    progressBox.addEventListener("touchend", handleTouchEnd);
    progressBox.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      progressBox.removeEventListener("touchmove", handleTouchMove);
      progressBox.removeEventListener("touchend", handleTouchEnd);
      progressBox.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isDragging, progress, duration]);

  // 新增一个单独的 useEffect 来处理 timeupdate
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioAvailable) return;

    const handleTimeUpdate = () => {
      const currentAudio = audioRef.current;
      if (!currentAudio) return;

      setCurrentTime(currentAudio.currentTime);
      if (!isDragging) {
        setProgress((currentAudio.currentTime / currentAudio.duration) * 100);
      }
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioAvailable, isDragging]); // 添加必要的依赖项

  // 检查环境
  useEffect(() => {
    const hasNativeMethod = DSBridge.hasNativeMethod("route.navigation");
    setIsNative(hasNativeMethod);
    return () => {
      if (LoadingRef.current?.clear) {
        LoadingRef.current.clear();
      }
    };
  }, []);

  const handleTabChange = async (cat) => {
    if (activeCategory === cat) return;
    setActiveCategory(cat);

    // 如果当前类别的数据已经存在，就不重新请求
    if (audioList.some((item) => item.category === cat)) {
      return;
    }

    if (LoadingRef.current?.clear) {
      LoadingRef.current.clear();
    }
    Toast.clear();
    const tabLoading = Toast.loading({
      message: "正在加载...",
      forbidClick: true,
      duration: 0,
    });
    try {
      const res = await getAudioPreviewV3({ category: cat });
      if (res?.code === 200 && res.data) {
        // 将新请求到的数据拼接到原有的 audioList 中，而不是替换
        setAudioList((prev) => {
          // 过滤掉没有 category 的坏数据
          const newList = (res.data.list || []).filter((item) => item.category);
          // 过滤掉可能重复的数据，保留其他类别的数据并加入新类别的数据
          const prevFiltered = prev.filter((item) => item.category !== cat);
          console.log([...prevFiltered, ...newList]);
          return [...prevFiltered, ...newList];
        });
      } else {
        Toast.fail(res?.message || "列表加载失败");
      }
    } catch (err) {
      Toast.fail("列表加载失败");
    } finally {
      if (tabLoading && tabLoading.clear) {
        tabLoading.clear();
      }
    }
  };

  const handleSelectAudio = (item, autoPlay = false) => {
    setCurrentAudioId(item.audio_id);
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    }
    if (item.audio_url) {
      initAudio(item.audio_url, autoPlay);
    } else {
      setAudioAvailable(false);
      Toast.fail("暂无音频地址");
    }
  };

  const handlePrev = () => {
    if (!audioList.length) return;
    const currentAudio = audioList.find(
      (item) => item.audio_id === currentAudioId,
    );
    if (!currentAudio) return;

    const currentCategoryList = audioList.filter(
      (item) => item.category === currentAudio.category,
    );
    const currentIndex = currentCategoryList.findIndex(
      (item) => item.audio_id === currentAudioId,
    );

    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = currentCategoryList.length - 1;
    }

    const prevAudio = currentCategoryList[prevIndex];
    handleSelectAudio(prevAudio, isPlaying || loopMode === "list");
  };

  const handleNext = (isEnded = false) => {
    if (!audioList.length) return;
    const currentAudio = audioList.find(
      (item) => item.audio_id === currentAudioId,
    );
    if (!currentAudio) return;

    const currentCategoryList = audioList.filter(
      (item) => item.category === currentAudio.category,
    );
    const currentIndex = currentCategoryList.findIndex(
      (item) => item.audio_id === currentAudioId,
    );

    let nextIndex = currentIndex + 1;
    if (nextIndex >= currentCategoryList.length) {
      nextIndex = 0;
    }

    const nextAudio = currentCategoryList[nextIndex];
    handleSelectAudio(nextAudio, isEnded || isPlaying);
  };

  const toggleLoopMode = () => {
    const newMode = loopMode === "list" ? "single" : "list";
    setLoopMode(newMode);
    localStorage.setItem("utalk_audio_loop_mode", newMode);
    Toast.info(newMode === "list" ? "列表循环播放" : "单曲循环播放");
  };

  // 分开一个 useEffect 处理音频初始化
  useEffect(() => {
    if (isNative) {
      const id = Number(searchParams.get("id"));
      if (!id) return;

      setCurrentAudioId(id);

      const initData = async () => {
        try {
          const res = await getAudioPreviewV3({ audio_id: id });
          if (res?.code === 200 && res.data) {
            // 过滤掉空的分类
            const categories = (res.data.categories || []).filter(Boolean);
            setCategories(categories);

            // 过滤掉没有 category 的坏数据
            let initialList = (res.data.list || []).filter(
              (item) => item.category,
            );

            let currentAudio = initialList.find((item) => item.audio_id === id);
            if (!currentAudio && initialList.length > 0) {
              currentAudio = initialList[0];
            }

            // 容错：如果初始列表没有数据，但有分类，则尝试拉取第一个分类的数据
            if (!currentAudio && categories.length > 0) {
              const firstCategory = categories[0];
              const fallbackRes = await getAudioPreviewV3({
                category: firstCategory,
              });
              if (fallbackRes?.code === 200 && fallbackRes.data) {
                // 同样过滤掉坏数据
                initialList = (fallbackRes.data.list || []).filter(
                  (item) => item.category,
                );
                // 第一次进入时，如果原本列表没数据，仅拉取分类列表用于展示，不再自动加载并播放第一个音频
              }
            }

            setAudioList(initialList);

            if (currentAudio) {
              setCurrentAudioId(currentAudio.audio_id);
              setActiveCategory(currentAudio.category);
              if (currentAudio.audio_url) {
                initAudio(currentAudio.audio_url);
              } else {
                setAudioAvailable(false);
                Toast.fail("暂无音频地址，请退出重试");
              }
            } else {
              if (res.data.categories && res.data.categories.length > 0) {
                setActiveCategory(res.data.categories[0]);
              }
              setAudioAvailable(false);
              Toast.fail("未找到相关音频");
            }
          } else {
            setAudioAvailable(false);
            Toast.fail(res?.message || "音频加载失败，请稍后重试");
          }
        } catch (err) {
          setAudioAvailable(false);
          Toast.fail("音频加载失败，请稍后重试");
        }
      };

      initData();
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute("src");
        audioRef.current.load();
        setAudioAvailable(false);
      }
    };
  }, [isNative, searchParams]);

  // 监听 showList 和 activeCategory 变化，滚动到当前选中的音频位置
  useEffect(() => {
    if (showList) {
      // 延迟等待 DOM 渲染并显示
      const timer = setTimeout(() => {
        const activeElement = document.querySelector(
          `.${styles["list-item-active"]}`,
        );
        const listContainer = document.querySelector(
          `.${styles["popup-list"]}`,
        );
        
        if (activeElement && listContainer) {
          // 使用 scrollTop 计算来替代 scrollIntoView，兼容性更好
          const containerRect = listContainer.getBoundingClientRect();
          const elementRect = activeElement.getBoundingClientRect();
          
          // 计算需要滚动到的目标位置，使元素居中
          // 当前滚动位置 + 元素相对容器的偏移 - 容器一半的高度 + 元素一半的高度
          const targetScrollTop = listContainer.scrollTop + 
            (elementRect.top - containerRect.top) - 
            (containerRect.height / 2) + 
            (elementRect.height / 2);
            
          // 兼容旧版设备的滚动方式
          if (typeof listContainer.scrollTo === 'function') {
            listContainer.scrollTo({
              top: targetScrollTop,
              behavior: "smooth"
            });
          } else {
            // 最原始的兜底方案
            listContainer.scrollTop = targetScrollTop;
          }
        }
      }, 150); // 稍微增加一点延迟确保样式和布局计算已完成
      return () => clearTimeout(timer);
    }
  }, [showList, activeCategory]);

  return (
    <div className={styles["audio-preview-container"]}>
      {isNative ? (
        <>
          <audio
            ref={audioRef}
            loop={loopMode === "single"}
            onLoadedMetadata={() => {
              if (audioRef.current) {
                setDuration(audioRef.current.duration);
                setAudioAvailable(true);
                if (LoadingRef.current?.clear) {
                  LoadingRef.current.clear();
                }
                if (shouldAutoPlayRef.current) {
                  audioRef.current
                    .play()
                    .then(() => {
                      setIsPlaying(true);
                    })
                    .catch(() => {
                      setIsPlaying(false);
                    });
                  shouldAutoPlayRef.current = false;
                }
              }
            }}
            onError={(e) => {
              const audio = audioRef.current;
              if (audio) {
                const currentSrc = audio.currentSrc || audio.src;
                // 忽略没有 src 或 src 被解析为当前页面 URL（比如 src="" 或无 src 时的默认行为）时的报错
                if (!currentSrc || currentSrc === window.location.href) {
                  return;
                }
                // 忽略被主动终止的情况（比如在加载过程中切换音频）
                if (audio.error && audio.error.code === 1) {
                  return;
                }
              }

              setAudioAvailable(false);
              setIsPlaying(false);
              Toast.fail("音频加载失败，请稍后重试");
              if (LoadingRef.current?.clear) {
                LoadingRef.current.clear();
              }
            }}
            onEnded={() => {
              setProgress(0);
              setCurrentTime(0);
              setIsPlaying(false);
              if (loopMode === "list") {
                handleNext(true);
              }
            }}
          />
          <div className={styles["player-container"]}>
            <img
              className={styles["right-banner"]}
              src={require("@/images/login/right-banner.png")}
            />
            <CommonNavBar
              backgroundColor={"transparent"}
              onClickBack={() => {
                navigate(-1);
              }}
              title="音频学习"
            />
            <div className={styles["scroll-content"]}>
              <img
                className={styles["audio-pic"]}
                src={require("@/images/audio-preview/audio-pic.png")}
              />
              <div className={styles["audio-name"]}>
                {audioList.find((item) => item.audio_id === currentAudioId)
                  ?.alias || ""}
              </div>
              <div className={styles["audio-text"]}>
                <div>Listen to the audio and read aloud!</div>
                <div style={{ textAlign: "left" }}>边听音频边大声朗读哦！</div>
              </div>
              <>
                <div
                  className={styles["progress-box"]}
                  ref={progressBoxRef}
                  onTouchStart={handleTouchStart}
                >
                  <div
                    className={styles["current-progress"]}
                    style={{ width: calculatePixelPositions().progressWidth }}
                  ></div>
                  <div
                    className={styles["current-button"]}
                    ref={buttonRef}
                    style={{ left: calculatePixelPositions().buttonLeft }}
                  ></div>
                </div>
                <div className={styles["audio-time"]}>
                  {formatTime(currentTime)}/{formatTime(duration)}
                </div>
                <div className={styles["button-list"]}>
                  <img
                    className={styles["common-button"]}
                    src={require(
                      `@/images/audio-preview/cycle-${
                        loopMode === "list" ? "1" : "2"
                      }.png`,
                    )}
                    onClick={toggleLoopMode}
                  />
                  <div className={styles["center-list"]}>
                    <img
                      className={styles["common-button"]}
                      src={require("@/images/audio-preview/audio-prev.png")}
                      onClick={() => handlePrev()}
                    />
                    <img
                      className={styles["play-button"]}
                      src={require(
                        isPlaying
                          ? "@/images/audio-preview/audio-pause.png"
                          : "@/images/audio-preview/audio-play.png",
                      )}
                      onClick={togglePlay}
                    />
                    <img
                      className={styles["common-button"]}
                      src={require("@/images/audio-preview/audio-next.png")}
                      onClick={() => handleNext(false)}
                    />
                  </div>
                  <img
                    className={styles["common-button"]}
                    src={require("@/images/audio-preview/list-img.png")}
                    onClick={() => setShowList(true)}
                  />
                </div>
              </>
            </div>
          </div>
          {showList && (
            <div
              className={styles["custom-popup-overlay"]}
              onClick={() => setShowList(false)}
            >
              <div
                className={styles["custom-popup-content"]}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles["popup-header"]}>
                  <div className={styles["popup-title"]}>播放列表</div>
                  <img
                    className={styles["popup-close"]}
                    src={require("@/images/audio-preview/close-img.png")}
                    onClick={() => setShowList(false)}
                  />
                </div>
                <div className={styles["popup-tabs"]}>
                  {categories.map((cat) => (
                    <div
                      key={cat}
                      className={`${styles["tab-item"]} ${
                        activeCategory === cat ? styles["tab-item-active"] : ""
                      }`}
                      onClick={() => handleTabChange(cat)}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
                <div className={styles["popup-list"]}>
                  {audioList
                    .filter((item) => item.category === activeCategory)
                    .map((item) => (
                      <div
                        key={item.audio_id}
                        className={`${styles["list-item"]} ${
                          currentAudioId === item.audio_id
                            ? styles["list-item-active"]
                            : ""
                        }`}
                        onClick={() => handleSelectAudio(item, true)}
                      >
                        <div className={styles["item-name"]}>{item.alias}</div>
                        {currentAudioId === item.audio_id && (
                          <img
                            className={styles["item-select-icon"]}
                            src={require("@/images/audio-preview/select-img.png")}
                          />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className={styles["no-container"]}>
            <img
              className={styles["no-top"]}
              src={require("@/images/audio-preview/no-top.png")}
            />
            <div className={styles["no-text"]}>
              <div className={styles["no-text-1"]}>
                <div>{"请用口袋领航外教App"}</div>
                <div>{"扫码收听学习手册中的音频"}</div>
              </div>
              <div className={styles["no-text-2"]}>
                {"没有口袋领航外教？扫码下载！"}
              </div>
            </div>
            <div className={styles["no-bottom"]}>
              <img
                className={styles["no-bottom-code"]}
                src={require("@/images/audio-preview/no-code.png")}
              />
              <img
                className={styles["no-bottom-ubao"]}
                src={require("@/images/audio-preview/no-ubao.png")}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AudioPreview;
